/**
 * Anfragen und Angebote vor der Kooperation (0072): Stände der Anfrage, Preisliste je Creator, Angebotszeilen mit Zuschlägen,
 * Summen, Hinweis zu Steuer und Provision, Nachfassen. Vorstufen sind KEINE Kooperation: sie zählen nirgends mit.
 *
 * Gleich halten mit app.offer_line_amount, app.offer_surcharges_valid, app.inquiry_stage_after und app.offer_counter_key (0072):
 * Oberfläche, PDF und Datenbank rechnen dieselben Beträge.
 *
 * Rechenregel: Grundbetrag = Menge × Einzelpreis. Jeder Zuschlag ist ein Prozentsatz auf den Grundbetrag (nicht auf andere
 * Zuschläge), mal Anzahl der Einheiten (Monate Nutzungsrechte bzw. Exklusivität, zusätzliche Korrekturschleifen; pauschal = 1).
 * Jeder Zuschlag wird für sich kaufmännisch auf ganze Cent (bzw. Rappen) gerundet; die Zeile ist Grundbetrag plus Zuschläge.
 * So stimmen die Einzelbeträge im PDF immer mit der Summe überein.
 */
import { formatMoney } from "./money.js";
import type { Cents, Currency, IsoDate, Language, MoneyLocale } from "./types.js";

// ── Stände ───────────────────────────────────────────────────────────────────

export const INQUIRY_STAGES = ["inquiry", "offer_sent", "negotiation", "won", "declined", "lost"] as const;
export type InquiryStage = (typeof INQUIRY_STAGES)[number];
export const OPEN_INQUIRY_STAGES: readonly InquiryStage[] = ["inquiry", "offer_sent", "negotiation"];

export const INQUIRY_STAGE_LABEL: Readonly<Record<InquiryStage, { de: string; en: string }>> = {
  inquiry: { de: "Anfrage", en: "Inquiry" },
  offer_sent: { de: "Angebot gesendet", en: "Offer sent" },
  negotiation: { de: "Verhandlung", en: "Negotiation" },
  won: { de: "Zusage", en: "Won" },
  declined: { de: "Abgesagt", en: "Declined" },
  lost: { de: "Verloren", en: "Lost" },
};

export function isInquiryStage(v: unknown): v is InquiryStage {
  return typeof v === "string" && (INQUIRY_STAGES as readonly string[]).includes(v);
}
export function isOpenStage(stage: string): boolean {
  return (OPEN_INQUIRY_STAGES as readonly string[]).includes(stage);
}
export function inquiryStageLabel(stage: string, lang: Language = "de"): string {
  return isInquiryStage(stage) ? INQUIRY_STAGE_LABEL[stage][lang] : stage;
}

/** Stand eines Creators in einer Anfrage (Casting): vorgeschlagen, zugesagt (wird Kooperation), abgesagt (wir), verloren (Marke) */
export const CANDIDATE_STATUSES = ["proposed", "accepted", "declined", "lost"] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];
export const CANDIDATE_STATUS_LABEL: Readonly<Record<CandidateStatus, string>> = {
  proposed: "Vorgeschlagen", accepted: "Zusage", declined: "Abgesagt", lost: "Verloren",
};

/**
 * Stand der Anfrage nach den Entscheidungen je Creator (wie app.inquiry_stage_after): solange ein Creator offen ist, bleibt
 * der bisherige Stand. Sind alle entschieden: eine Zusage genügt für „Zusage“; sonst „Verloren“, sobald die Marke bei
 * einem Creator abgesagt hat, und „Abgesagt“, wenn nur wir (Agentur oder Creator) abgesagt haben.
 */
export function stageAfterDecisions(current: InquiryStage, candidates: readonly { status: string }[]): InquiryStage {
  if (candidates.length === 0 || candidates.some((c) => c.status === "proposed")) return current;
  if (candidates.some((c) => c.status === "accepted")) return "won";
  if (candidates.some((c) => c.status === "lost")) return "lost";
  return "declined";
}

// ── Zuschläge ────────────────────────────────────────────────────────────────

export const SURCHARGE_KINDS = ["usage", "exclusivity", "rush", "revision"] as const;
export type SurchargeKind = (typeof SURCHARGE_KINDS)[number];
export const SURCHARGE_LABEL: Readonly<Record<SurchargeKind, { de: string; en: string }>> = {
  usage: { de: "Nutzungsrechte (Paid Social)", en: "Usage rights (paid social)" },
  exclusivity: { de: "Exklusivität", en: "Exclusivity" },
  rush: { de: "Eilzuschlag", en: "Rush fee" },
  revision: { de: "Zusätzliche Korrekturschleife", en: "Additional revision round" },
};

/** Ein Zuschlag einer Zeile: Satz in Basispunkten (1 % = 100) je Einheit, Anzahl Einheiten, je Monat oder pauschal */
export interface Surcharge {
  kind: SurchargeKind;
  bp: number;
  units: number;
  /** Nur für die Anzeige: Satz gilt je Monat (Nutzungsrechte, Exklusivität); units = Monate */
  per_month?: boolean;
}

export const MAX_SURCHARGE_BP = 100_000; // 1000 %
export const MAX_SURCHARGE_UNITS = 120;

export function isSurchargeKind(v: unknown): v is SurchargeKind {
  return typeof v === "string" && (SURCHARGE_KINDS as readonly string[]).includes(v);
}

/** Zuschläge aus der Datenbank (jsonb) lesen; Ungültiges fällt weg */
export function surchargesFromJson(v: unknown): Surcharge[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((x): Surcharge[] => {
    if (!x || typeof x !== "object") return [];
    const o = x as Record<string, unknown>;
    const bp = Number(o.bp), units = Number(o.units);
    if (!isSurchargeKind(o.kind) || !Number.isInteger(bp) || !Number.isInteger(units)) return [];
    return [{ kind: o.kind, bp, units, ...(o.per_month === true ? { per_month: true } : {}) }];
  });
}

/** Fehlertexte für eine Zuschlagsliste (leer = gültig); dieselben Grenzen wie app.offer_surcharges_valid */
export function checkSurcharges(list: readonly Surcharge[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of list) {
    if (!isSurchargeKind(s.kind)) out.push(`Unbekannte Zuschlagsart: ${String(s.kind)}`);
    if (!Number.isInteger(s.bp) || s.bp < 0 || s.bp > MAX_SURCHARGE_BP) out.push("Zuschläge liegen zwischen 0 und 1000 %.");
    if (!Number.isInteger(s.units) || s.units < 1 || s.units > MAX_SURCHARGE_UNITS) out.push("Monate bzw. Schleifen: 1 bis 120.");
    if (seen.has(s.kind)) out.push(`${SURCHARGE_LABEL[s.kind]?.de ?? s.kind} steht doppelt.`);
    seen.add(s.kind);
  }
  return out;
}

/** Kaufmännisch runden (ab ,5 vom Nullpunkt weg) für ganze Zahlen n/d, d > 0 */
function roundDiv(n: number, d: number): number {
  const neg = n < 0;
  const a = Math.abs(n);
  const q = Math.floor(a / d);
  const r = a - q * d;
  const res = r * 2 >= d ? q + 1 : q;
  return neg ? -res : res;
}

/** Betrag eines Zuschlags: Grundbetrag × Satz × Einheiten, auf Cent gerundet */
export function surchargeCents(baseCents: Cents, s: Pick<Surcharge, "bp" | "units">): Cents {
  // Grundbetrag bis 10^11 Cent × 10^5 bp × 120 bleibt unter 2^63 nur mit BigInt sicher
  const n = BigInt(Math.round(baseCents)) * BigInt(s.bp) * BigInt(s.units);
  const d = 10_000n;
  const a = n < 0n ? -n : n;
  const q = a / d;
  const r = a % d;
  const res = r * 2n >= d ? q + 1n : q;
  return Number(n < 0n ? -res : res);
}

export interface OfferLineInput {
  quantity: number;
  unit_price_cents: Cents;
  surcharges: readonly Surcharge[];
}
export interface OfferLineAmounts {
  baseCents: Cents;
  surcharges: (Surcharge & { cents: Cents })[];
  totalCents: Cents;
}

/** Beträge einer Zeile (wie app.offer_line_amount) */
export function offerLineAmounts(line: OfferLineInput): OfferLineAmounts {
  const baseCents = Math.round(line.quantity) * Math.round(line.unit_price_cents);
  const surcharges = line.surcharges.map((s) => ({ ...s, cents: surchargeCents(baseCents, s) }));
  return { baseCents, surcharges, totalCents: baseCents + surcharges.reduce((a, s) => a + s.cents, 0) };
}

export interface OfferTotals {
  /** Summe der Zeilen */
  subtotalCents: Cents;
  /** Agenturprovision obendrauf (nur, wenn die Preise sie nicht enthalten) */
  feeCents: Cents;
  totalCents: Cents;
  /** Summe je Creator (Schlüssel: candidate_id) */
  byCandidate: Record<string, Cents>;
}

/** Summen des Angebots; die Provision obendrauf wird auf die Zwischensumme gerechnet und kaufmännisch gerundet */
export function offerTotals(lines: readonly (OfferLineInput & { candidate_id?: string | null })[], opts: { commissionIncluded: boolean; agencyFeeBp: number | null }): OfferTotals {
  const byCandidate: Record<string, Cents> = {};
  let subtotalCents = 0;
  for (const l of lines) {
    const t = offerLineAmounts(l).totalCents;
    subtotalCents += t;
    const k = l.candidate_id ?? "";
    byCandidate[k] = (byCandidate[k] ?? 0) + t;
  }
  const feeCents = opts.commissionIncluded || !opts.agencyFeeBp ? 0 : roundDiv(subtotalCents * opts.agencyFeeBp, 10_000);
  return { subtotalCents, feeCents, totalCents: subtotalCents + feeCents, byCandidate };
}

// ── Preisliste je Creator ────────────────────────────────────────────────────

export interface RateCard {
  currency: Currency;
  usage_bp: number | null;
  usage_per_month: boolean;
  exclusivity_bp: number | null;
  exclusivity_per_month: boolean;
  rush_bp: number | null;
  revision_bp: number | null;
}
export interface RateItem {
  platform: string;
  format: string;
  format_label: string | null;
  price_cents: Cents;
}

/** Was die Agentur beim Befüllen aus der Preisliste mitgibt (aus der Anfrage: Nutzungsrechte, Exklusivität, Eile) */
export interface SurchargeWish {
  usageMonths?: number | null;
  exclusivityMonths?: number | null;
  rush?: boolean;
  extraRevisions?: number | null;
}

/** Zuschläge einer Zeile aus der Preisliste; ohne Satz in der Preisliste entfällt der Zuschlag */
export function surchargesFromCard(card: RateCard | null | undefined, wish: SurchargeWish): Surcharge[] {
  if (!card) return [];
  const out: Surcharge[] = [];
  const months = (n: number | null | undefined) => Math.max(1, Math.min(MAX_SURCHARGE_UNITS, Math.round(n ?? 0)));
  if (card.usage_bp && wish.usageMonths && wish.usageMonths > 0) {
    out.push(card.usage_per_month ? { kind: "usage", bp: card.usage_bp, units: months(wish.usageMonths), per_month: true } : { kind: "usage", bp: card.usage_bp, units: 1 });
  }
  if (card.exclusivity_bp && wish.exclusivityMonths && wish.exclusivityMonths > 0) {
    out.push(card.exclusivity_per_month
      ? { kind: "exclusivity", bp: card.exclusivity_bp, units: months(wish.exclusivityMonths), per_month: true }
      : { kind: "exclusivity", bp: card.exclusivity_bp, units: 1 });
  }
  if (card.rush_bp && wish.rush) out.push({ kind: "rush", bp: card.rush_bp, units: 1 });
  if (card.revision_bp && wish.extraRevisions && wish.extraRevisions > 0) out.push({ kind: "revision", bp: card.revision_bp, units: months(wish.extraRevisions) });
  return out;
}

/** Ganze Monate zwischen zwei Tagen (angefangene Monate zählen voll), mindestens 1; für Exklusivität bis … */
export function monthsBetween(from: IsoDate, to: IsoDate): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || to < from) return 1;
  const [y1, m1, d1] = from.split("-").map(Number) as [number, number, number];
  const [y2, m2, d2] = to.split("-").map(Number) as [number, number, number];
  let n = (y2 - y1) * 12 + (m2 - m1);
  if (d2 >= d1) n += 1;
  return Math.max(1, Math.min(MAX_SURCHARGE_UNITS, n));
}

// ── Anzeige ──────────────────────────────────────────────────────────────────

function pct(bp: number, lang: Language): string {
  const s = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }).format(bp / 100);
  return lang === "en" ? `${s}%` : `${s} %`;
}

/** „Nutzungsrechte (Paid Social) · 3 Monate × 10 %“, „Eilzuschlag · 25 %“, „Zusätzliche Korrekturschleife · 2 × 10 %“ */
export function surchargeText(s: Surcharge, lang: Language = "de"): string {
  const label = SURCHARGE_LABEL[s.kind]?.[lang] ?? s.kind;
  if (s.kind === "revision") return `${label} · ${s.units} × ${pct(s.bp, lang)}`;
  if (s.per_month) return `${label} · ${s.units} ${lang === "en" ? (s.units === 1 ? "month" : "months") : s.units === 1 ? "Monat" : "Monate"} × ${pct(s.bp, lang)}`;
  return `${label} · ${lang === "en" ? "flat" : "pauschal"} ${pct(s.bp, lang)}`;
}

/** Satz einer Preisliste als Text: „10 % je Monat“, „25 % pauschal“, „–“ */
export function rateText(bp: number | null | undefined, perMonth: boolean, lang: Language = "de"): string {
  if (!bp) return "–";
  return `${pct(bp, lang)} ${perMonth ? (lang === "en" ? "per month" : "je Monat") : lang === "en" ? "flat" : "pauschal"}`;
}

/** Satz der Korrekturschleife als Text: „10 % je Schleife“ / „10% per round“ (gerechnet wird je Schleife), „–“ ohne Satz */
export function revisionRateText(bp: number | null | undefined, lang: Language = "de"): string {
  if (!bp) return "–";
  return `${pct(bp, lang)} ${lang === "en" ? "per round" : "je Schleife"}`;
}

/**
 * Hinweis zu Steuer und Provision im Angebot (Dauerstreit der Branche: klar sagen, was der Preis enthält).
 * Netto: zzgl. Umsatzsteuer (CH: MWST); brutto: inkl.; Kleinunternehmer ohne Ausweis. Provision: enthalten oder obendrauf mit Satz.
 */
export function priceBasisText(opts: {
  basis: "net" | "gross"; commissionIncluded: boolean; agencyFeeBp: number | null; country: string | null; smallBusiness?: boolean; lang?: Language;
}): string {
  const lang = opts.lang ?? "de";
  const ch = (opts.country ?? "").toUpperCase() === "CH" || (opts.country ?? "").toUpperCase() === "LI";
  const vat = lang === "en" ? "VAT" : ch ? "MWST" : "Umsatzsteuer";
  // Schweiz: „nicht mehrwertsteuerpflichtig“ statt Kleinunternehmerregelung (SAAS-R3-6)
  const tax = opts.smallBusiness
    ? ch ? (lang === "en" ? "No VAT is charged (not registered for VAT)." : "Kein Ausweis der MWST (nicht mehrwertsteuerpflichtig).")
      : lang === "en" ? "No VAT is charged (small business exemption)." : "Kein Ausweis der Umsatzsteuer (Kleinunternehmerregelung)."
    : opts.basis === "gross"
      ? lang === "en" ? `All prices include ${vat}.` : `Alle Preise inklusive ${vat}.`
      : lang === "en" ? `All prices are net, plus ${vat} at the statutory rate.` : `Alle Preise netto zuzüglich ${vat} in gesetzlicher Höhe.`;
  const fee = opts.commissionIncluded
    ? lang === "en" ? "The agency commission is included in the prices." : "Die Agenturprovision ist in den Preisen enthalten."
    : opts.agencyFeeBp
      ? lang === "en" ? `Plus agency commission of ${pct(opts.agencyFeeBp, lang)} on the fees (shown separately).`
        : `Zuzüglich Agenturprovision von ${pct(opts.agencyFeeBp, lang)} auf die Honorare (gesondert ausgewiesen).`
      : lang === "en" ? "The prices do not include an agency commission." : "Die Preise enthalten keine Agenturprovision.";
  return `${tax} ${fee}`;
}

/** Nachfassen fällig? Angebot gesendet, Stand noch „Angebot gesendet“, seit `days` Tagen ohne Antwort (0 = aus) */
export function followUpDue(stage: string, sentOn: IsoDate | null, days: number, today: IsoDate): boolean {
  if (stage !== "offer_sent" || !sentOn || days <= 0) return false;
  const d = new Date(`${sentOn.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10) <= today;
}

/** Wert offener Angebote je Währung (letzte gesendete Fassung je offener Anfrage) */
export function openOfferValue(rows: readonly { stage: string; currency: string; total_cents: number | null }[]): Record<string, Cents> {
  const out: Record<string, Cents> = {};
  for (const r of rows) if (isOpenStage(r.stage) && r.total_cents) out[r.currency] = (out[r.currency] ?? 0) + r.total_cents;
  return out;
}

/** Summen je Währung als Text: „12.400,00 € · 3.000,00 CHF“ */
export function moneyByCurrency(sums: Record<string, Cents>, locale: MoneyLocale): string {
  const keys = Object.keys(sums).filter((k) => sums[k]).sort();
  if (!keys.length) return formatMoney(0, "EUR", locale);
  return keys.map((k) => formatMoney(sums[k]!, k as Currency, locale)).join(" · ");
}
